import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getAtsJobCreate } from '@/store/atsJobCreate/atsJobCreateSelectors';

import { getAtsJobDetails } from '@/store/atsJobDetails';

/**
 * Custom hook to determine whether we need to re-trigger an API action
 * based on the number of applicants in the "Applied" stage.
 * Triggers API calls for interview stages and stage counts if not already available.
 */
export const useApplicantCount = (jobData: {
  id: string;
  status: string;
  job_status: string;
}) => {
  const [shouldRetrigger, setShouldRetrigger] = useState(false);
  const location = useLocation();

  // Check if this is a cloned job from URL params
  const searchParams = new URLSearchParams(location.search);
  const isClonedJob = searchParams.get('isClonedJob') === 'true';
  const isDraft = jobData.status === 'draft' || jobData.job_status === 'draft';
  const skipCount = isDraft || isClonedJob;

  // Select data from Redux store
  const { getInterviewPlan } = useSelector(getAtsJobCreate);
  const { getStagesCount } = useSelector(getAtsJobDetails);

  const { data = [] } = getInterviewPlan ?? {};
  const { data: { stage_counts = [] } = {} } = getStagesCount ?? {};
  const interviewStages = useMemo(() => data ?? [], [data]);

  // Interview plan and stage counts are fetched by the parent (JobCandidates)
  // via fetchDependentAPIsForCandidatesList. No need to fetch here independently
  // as it causes duplicate ats_job_stages and count_by_stage API calls.
  // The data will be available in Redux once the parent's fetch completes.

  // Determine whether to retrigger based on applicant count
  useEffect(() => {
    // For cloned jobs, never retrigger
    if (skipCount) {
      // Derived count written from an effect; see note in AiIdealCandidateChatBox.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldRetrigger(false);
      return;
    }

    if (interviewStages.length === 0 || stage_counts.length === 0) return;

    const applicationReview = interviewStages.find(
      (item) => item?.ats_job_milestone?.name === 'Applied'
    );

    if (!applicationReview?.id) {
      setShouldRetrigger(false);
      return;
    }

    const applicationReviewCount =
      stage_counts.find((item) => item.stage_id === applicationReview.id)
        ?.applications_count ?? 0;

    setShouldRetrigger(applicationReviewCount > 0);
  }, [interviewStages, skipCount, stage_counts]);

  return { shouldRetrigger };
};
